<template>
  <div>
    <v-row dense>
      <v-col cols="12">
        <DetailCard :title="$t('bidDetails')" :flat="filled" :elementClass="cardClass">
          <template #title.append>
            <Icon
            v-if="isBidExpired"
            icon="mdi-timer-alert-outline"
            iconColor="error"
            :tooltipText="$t('bidExpired')"/>
            <v-chip v-if="statusText" small :color="statusChipColor" text-color="white">
              {{ statusText }}
            </v-chip>
          </template>
          <v-row dense>
            <v-col cols="12" md="6" class="py-0" v-for="row in detailRows" :key="row.key">
              <v-row no-gutters>
                <v-col cols="6" sm="5">{{ $t(row.key) }}:</v-col>
                <v-col>
                  <span :style="valueStyle(row.value)">{{ row.value || $t('notSpecified') }}</span>
                </v-col>
              </v-row>
            </v-col>
          </v-row>
        </DetailCard>
      </v-col>
    </v-row>

    <v-row dense>
      <v-col cols="12" md="6">
        <DetailCard :title="$t('geography')" :flat="filled" :elementClass="cardClass">
          <BidLocationDisplay :spot="bid.spot || {}"/>
        </DetailCard>
      </v-col>
      <v-col cols="12" md="6">
        <DetailCard :title="$t('properties')" :flat="filled" :elementClass="cardClass">
          <v-row no-gutters v-for="row in propertyRows" :key="row.key">
            <v-col cols="6" sm="5">{{ $t(row.key) }}:</v-col>
            <v-col>
              <span :style="valueStyle(row.value)">{{ row.value || $t('notSpecified') }}</span>
            </v-col>
          </v-row>
        </DetailCard>
      </v-col>
    </v-row>

    <v-row dense class="mt-2 px-1" align="baseline">
      <v-col cols="auto">
        <span class="subtitle-1">{{ $t('products') }}</span>
      </v-col>
      <v-spacer/>
      <v-col cols="auto" v-if="flatProducts.length > 0">
        <span class="caption grey--text text--darken-1">
          {{ numberWithCommas(totalVolume, 3) }} t &bull; {{ formatMoney(totalEstimatedValue) }} {{ $t('estDelivered') }}
        </span>
      </v-col>
    </v-row>

    <NoData v-if="flatProducts.length === 0" class="my-4">{{ $t('noProductsYet') }}</NoData>

    <v-card v-else :outlined="!filled" :flat="filled" :class="cardClass">
      <v-card-text class="pt-2 px-3 pb-2">
        <DataTable
        hideHeader
        disablePagination
        showPointerCursor
        expandOnRowClick
        containerClass="pa-0"
        tableKey="bidProductId"
        :items="flatProducts"
        :headers="productHeaders"
        :customCells="productCustomCells"
        :itemCanExpandFunction="hasCostComponents"
        :itemClass="productRowClass"
        @expanded="expandedProducts = $event">
          <template #productName="{ item }">
            <v-icon class="mr-1" :style="{ visibility: hasCostComponents(item) ? 'visible' : 'hidden' }">{{ `mdi-chevron-${isExpanded(item) ? 'up' : 'down'}` }}</v-icon>
            {{ item.productName }}
            <Icon
            v-if="isWon(item)"
            icon="mdi-trophy"
            iconColor="success"
            margin=""
            :tooltipText="$t('won')"/>
          </template>
          <template #harvestVolume="{ item }">
            <span class="text-no-wrap">{{ numberWithCommas(item.harvestVolume, 3) }} t</span>
          </template>
          <template #deliveredRate="{ item }">
            <Icon
            v-if="strictCosts && rateDivergesFromCosts(item)"
            icon="mdi-alert"
            iconColor="warning"
            :tooltipText="$t('bidRateDivergedFromCosts')"/>
            {{ formatMoney(item.deliveredRate) }}
          </template>
          <template #expanded-item="{ item }">
            <BidCostComponentTable :costComponents="item.costComponents"/>
          </template>
        </DataTable>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import { utcToLocalDate } from '@/utils/DateFormatter.js'
import { formatMoney, numberWithCommas } from '@/utils/NumericMutations'
import { Outcome, RfbStatus, BidProductStatus, BidCostTypeMode } from '@/utils/Enumerations.js'
import { helpers } from '@/plugins/theme.js'
import { rateDivergesFromCosts } from '@/model/Bid.js'
import BidHeaders from '@/headers/Bid.js'

export default {
  name: 'BidDetail',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    DetailCard: () => import('@/components/core/DetailCard.vue'),
    NoData: () => import('@/components/core/NoData.vue'),
    DataTable: () => import('@/components/core/table/DataTable.vue'),
    BidLocationDisplay: () => import('@/components/bid/BidLocationDisplay.vue'),
    BidCostComponentTable: () => import('@/components/bid/BidCostComponentTable.vue')
  },

  props: {
    bid: {
      type: Object,
      required: true
    },
    filled: {
      type: Boolean,
      default: false
    }
  },

  data: () => ({
    expandedProducts: [],
    fetchedTract: null
  }),

  watch: {
    'bid.tractId': {
      immediate: true,
      async handler (tractId) {
        this.fetchedTract = null
        if (!tractId || this.allTracts.some(t => t.tractId === tractId)) return
        const tract = await this.fetchTract(tractId)
        if (tractId === this.bid?.tractId) this.fetchedTract = tract
      }
    }
  },

  computed: {
    ...mapGetters('account', ['allAccounts']),
    ...mapGetters('tract', ['allTracts', 'allTimberCategories', 'allConversions', 'allLandownerTypes', 'allBidCostTypes']),
    ...mapGetters('product', { storeProducts: 'allProducts' }),
    ...mapGetters('user', ['companyInfo']),

    strictCosts () {
      return this.allBidCostTypes.length > 0 && this.companyInfo?.bidCostTypeMode !== BidCostTypeMode.Mixed.value
    },

    cardClass () {
      return this.filled ? 'grey lighten-4' : undefined
    },

    hasOutcome () {
      return this.bid?.outcome !== null && this.bid?.outcome !== undefined
    },

    outcomePending () {
      return this.bid?.rfbStatus === RfbStatus.Submitted.value || this.bid?.rfbStatus === RfbStatus.Complete.value
    },

    statusText () {
      return this.hasOutcome ? Outcome.fromInt(this.bid.outcome) : undefined
    },

    statusChipColor () {
      switch (this.bid?.outcome) {
        case Outcome.Won.value: return 'success'
        case Outcome.Lost.value: return 'error'
        default: return 'info'
      }
    },

    isBidExpired () {
      return !this.hasOutcome && this.outcomePending &&
        !!this.bid?.bidExpiration && new Date(this.bid.bidExpiration) < new Date()
    },

    detailRows () {
      const bid = this.bid
      return [
        { key: 'tract', value: bid.tractId ? this.tractName(bid.tractId) : this.$t('unassigned') },
        { key: 'supplier', value: this.accountName(bid.supplierAccountId) },
        { key: 'destination', value: this.accountName(bid.destinationAccountId) },
        { key: 'distance', value: bid.distance !== null && bid.distance !== undefined ? this.$t('milesShort', { distance: bid.distance }) : undefined },
        { key: 'bidDate', value: bid.bidDate ? utcToLocalDate(bid.bidDate) : undefined },
        { key: 'bidExpiration', value: bid.bidExpiration ? utcToLocalDate(bid.bidExpiration) : undefined },
        { key: 'rfbStatus', value: bid.rfbStatus !== null && bid.rfbStatus !== undefined ? RfbStatus.fromInt(bid.rfbStatus) : undefined },
        ...(bid.reason ? [{ key: 'reason', value: bid.reason }] : []),
        { key: 'harvestWindow', value: this.harvestWindowText }
      ]
    },

    propertyRows () {
      const bid = this.bid
      return [
        { key: 'timberCategory', value: this.allTimberCategories.find(t => t.timberCategoryId === bid.timberCategoryId)?.name },
        { key: 'conversion', value: this.allConversions.find(c => c.conversionId === bid.conversionId)?.name },
        { key: 'landownerType', value: this.allLandownerTypes.find(l => l.landownerTypeId === bid.landownerTypeId)?.name }
      ]
    },

    harvestWindowText () {
      const { harvestStartDate, harvestEndDate } = this.bid
      if (!harvestStartDate && !harvestEndDate) return undefined
      return `${utcToLocalDate(harvestStartDate)} - ${utcToLocalDate(harvestEndDate)}`
    },

    flatProducts () {
      return (this.bid?.products ?? []).map(p => ({
        ...p,
        productName: this.storeProducts.find(sp => sp.productId === p.productId)?.name
      }))
    },

    productHeaders () {
      return BidHeaders.bidProductHeaders(false)
    },

    productCustomCells () {
      return [
        { slotName: 'productName', value: 'productName' },
        { slotName: 'harvestVolume', value: 'harvestVolume' },
        { slotName: 'deliveredRate', value: 'deliveredRate' }
      ]
    },

    totalVolume () {
      return (this.bid?.products ?? []).reduce((total, p) => total + (p.harvestVolume ?? 0), 0)
    },

    totalEstimatedValue () {
      return (this.bid?.products ?? []).reduce((total, p) => total + (p.harvestVolume ?? 0) * (p.deliveredRate ?? 0), 0)
    }
  },

  methods: {
    ...mapActions('tract', ['fetchTract']),
    formatMoney,
    numberWithCommas,
    rateDivergesFromCosts,

    isWon (product) {
      return product.status === BidProductStatus.Won.value
    },

    hasCostComponents (product) {
      return product.costComponents.length > 0
    },

    isExpanded (product) {
      return this.expandedProducts.some(p => p.bidProductId === product.bidProductId)
    },

    productRowClass (product) {
      return this.hasCostComponents(product) ? '' : 'cursor-default'
    },

    valueStyle (value) {
      return { color: value ? undefined : helpers.getTranslucent(25) }
    },

    accountName (accountId) {
      if (accountId === null || accountId === undefined) return undefined
      return this.allAccounts.find(a => a.accountId === accountId)?.name
    },

    tractName (tractId) {
      return this.allTracts.find(t => t.tractId === tractId)?.name ?? this.fetchedTract?.name
    }
  }
}
</script>
